<template>
	<div class="navigator-header p-mb-3">
		<p class="title p-my-2">{{ $t("Squeeze.Reports.AutoValidationDocuments") }}</p>
	</div>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-12 p-sm-6 p-md-3 p-lg-3 p-xl-12">
			<Calendar
				id="range"
				v-model="selectedDate"
				selectionMode="range"
				:maxDate="maxDate"
				:manualInput="false"
				:showIcon="true"
				:placeholder="datePlaceholder"
			>
				<template #value="slotProps">
					<div class="date-item date-item-value" v-if="slotProps.selectedDate">
						<div>{{ slotProps.selectedDate }}</div>
					</div>
					<span v-else>
                {{ slotProps.placeholder }}
            </span>
				</template>
			</Calendar>
		</div>
		<div class="p-field p-col-12 p-sm-6 p-md-3 p-lg-3 p-xl-12">
			<MultiSelect
				v-model="selectedDocumentClasses"
				:options="allDocumentClasses"
				optionValue="id"
				optionLabel="name"
				:filter="true"
				:autoFilterFocus="true"
				@show="showDropdownOverlay"
			>
				<template #value="slotProps">
					<div v-if="slotProps.value.length" v-tooltip.bottom="slotProps.value ? mapTooltipValues(slotProps.value) : ''">
						{{ getOptionLabel(slotProps.value) }}
					</div>
					<div v-else class="p-placeholder" data-pc-section="label">{{ $t('Squeeze.Reports.SelectDocumentClasses') }}</div>
				</template>
				<template #option="slotProps">
					{{ slotProps.option.name }}
				</template>
			</MultiSelect>
		</div>
		<div class="p-field p-col-12 p-sm-6 p-md-3 p-lg-3 p-xl-12">
			<IconField>
				<InputIcon class="mdi mdi-comment-question-outline" v-tooltip.top="$t('Squeeze.Reports.CompanyHelpText')" style="cursor: pointer;" />
				<InputText
					v-model="companyIds"
					id="companyIds"
					type="text"
					:placeholder="$t('Squeeze.Reports.Company')"
				/>
			</IconField>
		</div>
		<div class="p-field p-col-12 p-sm-6 p-md-3 p-lg-3 p-xl-12">
			<IconField>
				<InputIcon class="mdi mdi-comment-question-outline" v-tooltip.top="$t('Squeeze.Reports.CreditorHelpText')" style="cursor: pointer;" />
				<InputText
					v-model="creditorIds"
					id="creditorIds"
					type="text"
					:placeholder="$t('Squeeze.Reports.Creditor')"
				/>
			</IconField>
		</div>
		<ButtonGroup class="p-field p-col-12 p-sm-6 p-md-3 p-lg-3 p-xl-12 p-ml-auto" style="justify-content: flex-end;">
			<Button
				style="flex: 0; padding: 0.714rem 1.714rem;"
				icon="mdi mdi-filter-off-outline"
				v-tooltip.top="$t('Squeeze.General.ClearFilters')"
				@click="clearFilters"
			/>
			<Button
				icon="mdi mdi-filter-outline"
				:label="$t('Squeeze.Reports.Filter')"
				@click="filter"
			/>
		</ButtonGroup>
		<div class="p-col-12">
			<canvas id="autovalidationReportChart" ref="canvas"></canvas>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, onMounted, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ClientManager} from "@/singletons/ClientManager";
import ButtonGroup from 'primevue/buttongroup';
import MultiSelect from 'primevue/multiselect';
import Calendar from "primevue/calendar";
import {Chart, ChartDatasetCustomTypesPerDataset, ChartTypeRegistry, TooltipItem} from 'chart.js';
import moment from "moment";
import {DocumentClass, SimpleReport} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {formatDateRangePlaceholder} from "@/util/DateHelper";
import {ColorGenerator} from "@/util/ColorGenerator";
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';

export default defineComponent({
	name: "DocumentReportAutovalidation",
	methods: {showDropdownOverlay},
	components: {
		IconField,
		InputIcon,
		InputText,
		Calendar,
		MultiSelect,
		ButtonGroup,
	},
	setup() {
		const {t, locale} = useI18n();
		const toast = useToast();

		/** List of all DocumentClasses */
		const allDocumentClasses = ref<DocumentClass[]>([]);

		/** Currently selected Document-Classes by ID */
		const selectedDocumentClasses = ref<number[]>([]);

		/** Filter for creditor by creditor number */
		const creditorIds = ref<string>('');

		/** Filter for company by company number */
		const companyIds = ref<string>('');

		/** Selected date */
		const selectedDate = ref<string[]>([]);

		/** Chart data */
		const chartData = reactive<any>({});

		/** Chart labels */
		const chartLabels = ref<string[]>([]);

		/** Date placeholder */
		const datePlaceholder = ref<string>('');

		/** Max Date (current Date) */
		const maxDate = ref(new Date());

		/** Current color of client */
		const currentClientColor = ref<string>('');

		/** All Chart data*/
		const allReportData = reactive<SimpleReport>({});

		/** Service for Document Class API */
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		/** Service for report API */
		const reportApi = ClientManager.getInstance().squeeze.report;

		/**
		 * Get tooltip label for chart
		 * @param context
		 */
		const getTooltipLabel = (context: TooltipItem<keyof ChartTypeRegistry>) => {
			if (context.dataIndex === 0) {
				return ' ' + context.parsed.y + ' / ' + allReportData.validPercentage?.toFixed(2) + '%';
			} else if (context.dataIndex === 1) {
				return ' ' + context.parsed.y + ' / ' + allReportData.invalidPercentage?.toFixed(2) + '%';
			}
			return ' ' + context.parsed.y;
		}

		/** Get current chart datasets */
		const createData = () => {
			const data = [];
			let tempData = {};
			const dataEntries = Object.entries(chartData.value);
			const hexToHslColor = ColorGenerator.hexToHSL(currentClientColor.value);
			const hslColors = ColorGenerator.generateHslColors(dataEntries.length, hexToHslColor);

			let colorIndex = 0;
			for (const [key, value] of dataEntries) {
				const color = hslColors[colorIndex];
				colorIndex++;

				const dataFromChartOrigin: any = {value}.value;
				tempData = {
					label: {key}.key,
					data: dataFromChartOrigin[0]['data'],
					borderWidth: 3,
					borderColor: color,
					backgroundColor: color,
				}
				data.push(tempData);
			}
			return data;
		}

		/** Create the chart */
		const createChart = () => {
			const canvas = document.getElementById('autovalidationReportChart') as HTMLCanvasElement;
			const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
			currentClientColor.value = window.getComputedStyle(canvas).getPropertyValue('--dex-secondary-dark-color');

			new Chart(ctx, {
				type: 'bar',
				data: {
					labels: chartLabels.value,
					datasets: createData() as ChartDatasetCustomTypesPerDataset[],
				},
				options: {
					scales: {
						y: {
							beginAtZero: true,
						},
					},
					plugins: {
						tooltip: {
							callbacks: {
								label: (tooltipItem: TooltipItem<keyof ChartTypeRegistry>) => getTooltipLabel(tooltipItem),
							},
						},
					},
				},
			});
		}

		/**
		 * Get Chart Data
		 * @param startDate
		 * @param endDate
		 */
		const getChartData = async (startDate: moment.Moment, endDate: moment.Moment) => {
			try {
				datePlaceholder.value = formatDateRangePlaceholder(startDate, endDate, locale.value);
				const reportData = await reportApi.getSimpleExtractionReport(
					selectedDocumentClasses.value.toString(),
					creditorIds.value,
					companyIds.value,
					startDate.format(),
					endDate.format()
				);

				// Set all report data
				Object.assign(allReportData, reportData);

				// Build labels for x axis
				chartLabels.value = [t('Squeeze.Reports.Autovalidation'), t('Squeeze.Reports.ManuallyCorrected')];

				// Build import data
				const dataLabelString: string = t('Squeeze.Reports.TotalValidations') +  ' ' + reportData.numberOfProcessedDocuments;
				chartData.value = {
					[dataLabelString]: [
						{
							"data": [
								reportData.numberOfValidDocuments,
								reportData.numberOfInvalidDocuments,
							],
						},
					],
				};

				// Build Chart
				const presentChart = Chart.getChart("autovalidationReportChart");
				if (presentChart) {
					presentChart.destroy();
					createChart();
				} else {
					createChart();
				}
			} catch (err: any) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), err);
			}
		}

		/** Clear filters */
		const clearFilters = () => {
			selectedDate.value = [];
			selectedDocumentClasses.value = [];
			creditorIds.value = '';
			companyIds.value = '';
			getChartData(moment('01.01.', 'DD MM'), moment());
		}

		/** Triggered to filter */
		const filter = () => {
			if (selectedDate.value[1]) {
				getChartData(moment(selectedDate.value[0]), moment(selectedDate.value[1]).endOf('day'));
			} else {
				// when endDate not set, then set endDate to startDate
				getChartData(moment(selectedDate.value[0]), moment(selectedDate.value[0]).endOf('day'));
			}
		}

		/** Get all DocumentClasses */
		const getAllDocumentClasses = () => {
			documentClassApi.getAllDocumentClasses()
				.then((documentClasses) => {
					allDocumentClasses.value = documentClasses;
				})
				.catch((error: {message: string}) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), error.message);
				})
		}

		/**
		 * Triggered when the tooltip is shown
		 * @param values
		 */
		const mapTooltipValues = (values: string[]) => {
			return values
				.map((value: string) => allDocumentClasses.value.find(docClass => docClass.id === Number(value))?.name)
				.join(", ");
		}

		/**
		 * Get option label of selected document class
		 * @param options
		 */
		const getOptionLabel = (options: number[]) => {
			let label = '';
			options.forEach((option: number, index: number) => {
				if (index <= 2) {
					const currentDocClass = allDocumentClasses.value.find(docClass => docClass.id === option);
					if (currentDocClass) {
						label += currentDocClass.name;
						if (options.length > 1 && index < options.length - 1) {
							label += ', ';
						}
					}
				} else if (index === 3) {
					label += '...';
				}
			})

			return label;
		}

		onMounted(() => {
			getAllDocumentClasses();
		})

		onBeforeMount(async () =>{
			await getChartData(moment('01.01.', 'DD MM'), moment());
		})

		return {
			t,
			locale,
			toast,
			allDocumentClasses,
			selectedDocumentClasses,
			creditorIds,
			companyIds,
			selectedDate,
			chartData,
			chartLabels,
			datePlaceholder,
			maxDate,
			allReportData,
			clearFilters,
			filter,
			getTooltipLabel,
			mapTooltipValues,
			getOptionLabel,
		};
	},
});

</script>

<style scoped>

.navigator-header {
	color: var(--dex-primary-color);
}

.title {
	font-size: 2rem;
}

::v-deep(.p-multiselect .p-multiselect-label) {
	padding: 0.7rem 0.7rem;
}

::v-deep(.p-multiselect-label-empty) {
	overflow: unset;
	visibility: unset;
}

::v-deep(.p-calendar-w-btn) {
	border: none !important;
}

</style>

